import { useEffect, useState, useMemo, useCallback, lazy, ComponentProps } from 'react';
import { isCompleted, isSaved, isVerified as isVerifiedUtil } from '@alltrails/modules/Lists/listUtils';
import { type ListsState, setListsAndListItems } from '@alltrails/redux/slices/lists';
import { ListItem } from '@alltrails/shared/types/lists';
import { PrivacyPolicyOptions } from '@alltrails/shared/types/privacyPolicy';
import { useDispatch, useSelector } from 'react-redux';
import { NoSsrSuspense } from '@alltrails/core';
import type Context from '@alltrails/core/types/Context';
import useUser from '@alltrails/context/hooks/useUser';
import { useModal } from '@alltrails/modal';
import { useLanguageRegionCode } from '@alltrails/language/hooks/useLanguageRegionCode';
import { type AnalyticsInfo } from '../utils/listAmplitudeHelpers';
import getListsAndListItems from '../utils/requests';

const ReduxSaveToListModal = lazy(() => import('../components/ReduxSaveToListModal'));
const SignUpModal = lazy(() => import('@alltrails/user-auth/components/SignUpModal'));

type ItemType = ListItem['type'];

type SignUpInfo = {
  onSuccess?: () => void;
};

/**
 * @param signUpInfo
 * @param preventAutoInitialize boolean to prevent list fetching on successful sign in. NOTE: setting `true` may require you to manually initialize lists.
 * @param analyticsInfo additional analytics info to be passed to the modal
 * @returns
 */
const useReduxListItems = (signUpInfo?: SignUpInfo, preventAutoInitialize?: boolean, analyticsInfo?: AnalyticsInfo) => {
  const dispatch = useDispatch();
  const currentUser = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const { listItems } = useSelector((state: { lists: ListsState; context: Context }) => ({
    listItems: state.lists.listItems,
    currentUser: state.context.currentUser,
    languageRegionCode: state.context.languageRegionCode
  }));
  const [{ id, type, contentPrivacy, isCustomRoute }, setIdTypeAndPrivacy] = useState<{
    id: number;
    type: ItemType;
    contentPrivacy?: PrivacyPolicyOptions;
    isCustomRoute?: boolean;
  }>({
    id: 0,
    type: '' as ItemType,
    contentPrivacy: '' as PrivacyPolicyOptions,
    isCustomRoute: false
  });
  const [shouldOpenListModal, setShouldOpenListModal] = useState(false);

  const { modal, openModal } = useModal(
    ReduxSaveToListModal,
    useMemo(() => ({ id, type, contentPrivacy, analyticsInfo, isCustomRoute }), [id, type, contentPrivacy, analyticsInfo, isCustomRoute])
  );

  const onSuccess = useCallback(async () => {
    if (preventAutoInitialize) {
      setShouldOpenListModal(true);
    } else {
      const payload = await getListsAndListItems();
      dispatch(setListsAndListItems(payload));
      openModal();
    }

    signUpInfo?.onSuccess?.();
  }, [dispatch, openModal, preventAutoInitialize, signUpInfo]);

  const { modal: signUpModal, openModal: openSignupModal } = useModal(
    SignUpModal,
    useMemo<Omit<ComponentProps<typeof SignUpModal>, 'closeModal'>>(
      () => ({
        autoClose: true,
        isOpen: true,
        languageRegionCode,
        onSuccess,
        trigger: 'Favorite',
        upsellTrigger: 'Favorite'
      }),
      [languageRegionCode, onSuccess]
    )
  );

  const handleFavoriteClick = useCallback(
    (itemType: ItemType, itemId: number, itemPrivacy?: PrivacyPolicyOptions, isCustomRoute?: boolean) => {
      // Update state before opening the signup modal so that if signup is successful we can automatically open the save to list modal
      setIdTypeAndPrivacy({ id: itemId, type: itemType, contentPrivacy: itemPrivacy, isCustomRoute });

      if (!currentUser) {
        openSignupModal();
        return;
      }

      openModal();
    },
    [currentUser, openModal, openSignupModal]
  );

  useEffect(() => {
    if (shouldOpenListModal && !!listItems) {
      openModal();
      setShouldOpenListModal(false);
    }
  }, [listItems, openModal, shouldOpenListModal]);

  const isFavorite = useCallback((itemType: string, itemId: number) => isSaved(listItems, itemType, itemId), [listItems]);
  const isComplete = useCallback((itemType: string, itemId: number) => isCompleted(listItems, itemType, itemId), [listItems]);
  const isVerified = useCallback((itemType: string, itemId: number) => isVerifiedUtil(listItems, itemType, itemId), [listItems]);
  return {
    isFavorite,
    isComplete,
    isVerified,
    handleFavoriteClick,
    listModal: <NoSsrSuspense fallback={null}>{modal}</NoSsrSuspense>,
    signUpModal: <NoSsrSuspense fallback={null}>{signUpModal}</NoSsrSuspense>
  };
};

export default useReduxListItems;
